/**
 * ehr#727 (PEER_SUPPORT_NOTE_10-30-V8): the Peer support note's own parts, the pure pieces the
 * editor and the printout read (the parts themselves are in components/PeerSupportFields).
 */
import { PEER_CREDENTIAL_LABEL, PEER_SUPPORT_KIND } from '@ehr/shared';

/** One activity pill: its name and whether it is picked. */
export interface ActivityPill {
  name: string;
  on: boolean;
}

/**
 * The pills the note shows (V8 peerActFilter): the practice's list, then anything picked that is
 * no longer on it; "Search or add an activity" narrows them by name, and a picked one always stays.
 */
export function activityPills(
  items: readonly string[],
  picked: readonly string[],
  query: string,
): ActivityPill[] {
  const has = (l: readonly string[], n: string) =>
    l.some((x) => x.toLowerCase() === n.toLowerCase());
  const all = [...items, ...picked.filter((p) => !has(items, p))];
  const q = query.trim().toLowerCase();
  return all
    .map((name) => ({ name, on: has(picked, name) }))
    .filter((p) => p.on || !q || p.name.toLowerCase().includes(q));
}

/** A picked list with this activity turned on or off (the list's own spelling, once). */
export function togglePicked(picked: readonly string[], name: string): string[] {
  const at = picked.findIndex((x) => x.toLowerCase() === name.toLowerCase());
  return at >= 0 ? picked.filter((_, i) => i !== at) : [...picked, name];
}

/** "2026-10-13" (or a full ISO time) -> "10/13/2026"; '' for anything else. */
export function mdy(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/**
 * V8 12a: the Recovery goal's "From MM/DD/YYYY" tag. It shows while the box still holds the last
 * signed Peer support note's goal word for word (carried forward, not edited), never once it
 * changes. '' when there is nothing to tag.
 */
export function recoveryFromTag(text: string, last: { text: string; date: string } | null): string {
  if (!last || !last.text.trim() || text.trim() !== last.text.trim()) return '';
  const d = mdy(last.date);
  return d ? `From ${d}` : '';
}

const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

/**
 * V8 print: the Signatures card's peer specialist credential line on a Peer support note
 * ("Peer specialist credential: CPSS", or None on file), '' on any other note.
 */
export function peerCredentialPrintHtml(body: Record<string, unknown>): string {
  if (body.noteType !== PEER_SUPPORT_KIND) return '';
  const c = typeof body.peerCredential === 'string' ? body.peerCredential.trim() : '';
  return `<div class="sig-row"><div>${esc(PEER_CREDENTIAL_LABEL)}: <b>${c ? esc(c) : 'None on file'}</b></div></div>`;
}

/** The credential picker's choices: the practice's list, and the current one if it isn't on it. */
export function credentialChoices(list: readonly string[], current: string): string[] {
  const c = current.trim();
  return c && !list.some((x) => x.toLowerCase() === c.toLowerCase()) ? [...list, c] : [...list];
}
